'use client';

import { Box, Button, CircularProgress, IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import { apiResponse } from '@repo/api';
import { formatter, isValidDni } from '@repo/utils';
import { useRef, useState } from 'react';
import { MaterialIcon } from '../../components';

const CREDENTIALS_ERROR = 'Usuario y/o contraseña incorrectos';

export const LOGIN_SERVER_ERROR = 'No pudimos iniciar sesión. Intentá de nuevo en unos minutos.';
export const LOGIN_NETWORK_ERROR = 'No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.';

/**
 * Message for a login that failed for reasons other than wrong credentials: connectivity or a server error.
 *
 * @param {unknown} error - What the login `onError` callback received (a failed response or a thrown error).
 */
export const loginErrorMessage = (error?: unknown) =>
  (typeof navigator !== 'undefined' && !navigator.onLine) || error instanceof TypeError
    ? LOGIN_NETWORK_ERROR
    : LOGIN_SERVER_ERROR;

type LoginProps<T, D> = {
  noPassword?: boolean;
  credentialType?: 'username' | 'documentNumber' | 'taxpayerId';
  useCredentialType?: boolean;
  connector?: (_data: D) => Promise<apiResponse<T>>;
  callback?: (_connectorResponse: T, username?: string) => void;
  onError?: (_connectorResponse: T) => void;
  /** Wrong-credentials message, shown once under the password field. */
  credentialsErrorText?: string;
  forgotPassword?: () => void;
  extraButtons?: {
    label: string;
    onClick: () => void;
  }[];
};

/**
 * A reusable login component that handles user authentication.
 * It also features validation for all fields and a loading state.
 *
 * @param {Object} connector - A function that connects to an API to authenticate the user.
 * @param {Function} callback - An optional callback function to handle the API response.
 * @param {'email' | 'documentNumber'} credentialType - The type of credential to use in the login form @default email.
 * @param {Object} extraButtons - An optional array of extra buttons to display in the login form.
 * @return {React.JSX.Element} A JSX element representing the login form.
 */
export const Login = <T, D>({
  noPassword,
  useCredentialType = false,
  connector,
  callback,
  credentialType = 'documentNumber',
  extraButtons,
  forgotPassword,
  onError,
  credentialsErrorText = CREDENTIALS_ERROR,
}: LoginProps<T, D>) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<{ username?: string; password?: string }>({});
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Validation functions for different fields
  const validateDocument = (doc: string) => isValidDni(doc) || doc === '';
  const validateTaxpayerId = (taxId: string) => /^\d{11}$/.test(taxId) || taxId === '';

  // Function that validates each field on blur
  const handleBlur = () => {
    if (credentialType === 'documentNumber' && !validateDocument(username.replace(/\D/g, ''))) {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['username']: 'Ingresá un DNI válido' }));
    }
    if (credentialType === 'taxpayerId' && !validateTaxpayerId(username.replace(/\D/g, ''))) {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['username']: 'Ingresá un CUIT válido' }));
    }
    if (credentialType !== 'documentNumber' && credentialType !== 'taxpayerId' && username !== '') {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['username']: '' }));
    }
    if (password !== '') {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['password']: '' }));
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    usernameRef.current?.blur();
    passwordRef.current?.blur();
    if (!username) {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['username']: 'Este campo es requerido' }));
    }
    if (!password && !noPassword) {
      setFormErrors((prevErrors) => ({ ...prevErrors, ['password']: 'Este campo es requerido' }));
    }
    if (
      Object.entries(formErrors).some((content) => content[1] !== '') ||
      loading ||
      !username ||
      (!password && !noPassword)
    ) {
      return false;
    }
    setLoading(true);

    const requestBody = useCredentialType ? { [credentialType]: username } : { username, password };

    connector?.(requestBody as D)
      .then((connectorResponse) => {
        if (connectorResponse.notAuth) {
          throw new Error(connectorResponse.message || 'Invalid authentication');
        }
        if (connectorResponse.hardError) {
          setLoading(false);
          onError?.(connectorResponse);
        } else if (connectorResponse.softError) {
          setLoading(false);
          setFormErrors({
            username: credentialsErrorText,
            password: noPassword ? undefined : credentialsErrorText,
          });
        } else {
          callback?.(connectorResponse, username);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        onError?.(error);
      });
  };

  // Clears only the edited field's error; a failed-credentials error belongs to both fields, so it clears both
  const clearFieldError = (field: 'username' | 'password') =>
    setFormErrors((prevErrors) =>
      prevErrors[field] === credentialsErrorText ? { username: '', password: '' } : { ...prevErrors, [field]: '' },
    );

  // Wrong credentials are explained once, under the password; the username field is only marked invalid
  const usernameHelperText =
    !noPassword && formErrors.username === credentialsErrorText ? undefined : formErrors.username;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      data-testid="login-form"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
    >
      <Stack
        sx={{
          gap: 2,
          flex: 1,
        }}
      >
        <Typography variant="h1">Iniciar sesión</Typography>

        {credentialType === 'username' ? (
          <TextField
            ref={usernameRef}
            onChange={(e) => {
              setUsername(e.target.value);
              clearFieldError('username');
            }}
            value={username}
            label="Nombre de usuario"
            autoComplete="username"
            name="username"
            autoFocus
            placeholder="Ej: juan.perez"
            onBlur={handleBlur}
            error={!!formErrors.username}
            helperText={usernameHelperText}
            slotProps={{ htmlInput: { inputMode: 'text', autoComplete: 'username', 'data-testid': 'username' } }}
          />
        ) : credentialType === 'documentNumber' ? (
          <TextField
            ref={usernameRef}
            onChange={(e) => {
              setUsername(e.target.value.replace(/\D/g, ''));
              clearFieldError('username');
            }}
            value={formatter(username, 'dni')}
            label="DNI"
            autoComplete="username"
            name="username"
            autoFocus
            placeholder="Ej: 12345678"
            onBlur={handleBlur}
            error={!!formErrors.username}
            helperText={usernameHelperText}
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric', 'data-testid': 'documentNumber' } }}
          />
        ) : credentialType === 'taxpayerId' ? (
          <TextField
            ref={usernameRef}
            onChange={(e) => {
              setUsername(e.target.value.replace(/\D/g, ''));
              clearFieldError('username');
            }}
            value={formatter(username, 'cuit')}
            label="CUIT"
            autoComplete="username"
            name="username"
            autoFocus
            placeholder="Ej: 20-12345678-9"
            onBlur={handleBlur}
            error={!!formErrors.username}
            helperText={usernameHelperText}
            slotProps={{ htmlInput: { maxLength: 13, inputMode: 'numeric', 'data-testid': 'taxpayerId' } }}
          />
        ) : null}

        {!noPassword && (
          <TextField
            ref={passwordRef}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            value={password}
            label="Contraseña"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Contraseña"
            onBlur={handleBlur}
            error={!!formErrors.password}
            helperText={formErrors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={showPassword}
                    data-testid="show-password"
                  >
                    {showPassword ? <MaterialIcon icon="visibility_off" /> : <MaterialIcon icon="visibility" />}
                  </IconButton>
                ),
              },
              htmlInput: { autoComplete: 'current-password', 'data-testid': 'password' },
            }}
          />
        )}

        {forgotPassword && (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="none"
            onClick={forgotPassword}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textAlign: 'inherit',
            }}
          >
            Olvidé mi contraseña
          </Link>
        )}

        {/* Ingresar and the secondary actions (e.g. Activar mi cuenta) stay together under the fields, as in
         customer-mobile, so the main action stays above the phone keyboard */}
        <Stack sx={{ gap: 2, mt: 2 }}>
          <Button type="submit" disabled={loading} fullWidth variant="contained" data-testid="login">
            {loading ? (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={20} aria-hidden="true" />
                Cargando...
              </Box>
            ) : (
              <span>Ingresar</span>
            )}
          </Button>
          {extraButtons?.map((button) => (
            <Button
              type="button"
              key={button.label}
              onClick={(e) => {
                e.preventDefault();
                button.onClick();
              }}
              fullWidth
              variant="inverted"
            >
              {button.label}
            </Button>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
